import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { nowToTimeStr } from '@common/index';
import { IUpdateIndexRequest } from '@common/api/crypto-wallet';
import { ePlatforms } from '@common/networks';

import { StorageService } from '../storage/storage.service';

//TODO: add ICryptoWallet to common
type ICryptoWallet = any;
type ICryptoWalletWithWallet = any;

@Injectable({
    providedIn: 'root'
})
export class CryptoWalletApi
{

    constructor(
        private storage: StorageService,
        private http: HttpClient
    )
    {

    }

    public async getCryptoWallets(): Promise<ICryptoWallet[]>
    {
        try
        {
            const response = await this.http.get('/api/crypto-wallet').toPromise() as ICryptoWallet[];
            return response;
        } catch (error)
        {
            console.error(error);
            return undefined;
        }
    }

    public async getCryptoWalletsOnce(id: string): Promise<ICryptoWallet | null>
    {
        try
        {
            return await this.http.get<ICryptoWallet>(`/api/crypto-wallet/${ id }`).toPromise();
        } catch (error)
        {
            console.error(error);
            return null;
        }
    }

    // public async getCryptoWalletsWithWallets(): Promise<ICryptoWalletWithWallet[]>
    // {
    //     try
    //     {
    //         const url = '/api/crypto-wallet/with-wallet';
    //         return await this.http.get<ICryptoWalletWithWallet[]>(url).toPromise();
    //     } catch (error)
    //     {
    //         console.error(error);
    //         return [];
    //     }
    // }

    public async setCryptoWallet(
        name: string,
        phrase: boolean,
        backup: boolean = false,
        base = false,
        platform: ePlatforms = null,
        collapsed: boolean = null,
        index: number = null,
        hwType: string = null,
        source: string = null
    ): Promise<ICryptoWallet>
    {
        try
        {
            const response = await this.http.post<ICryptoWallet>('/api/crypto-wallet', {
                name,
                phrase,
                backup,
                base,
                platform,
                collapsed,
                order_index: index,
                hw_type: hwType,
                source
            }).toPromise();

            return { ...response, platform };
        } catch (error)
        {
            console.error(error);
            return undefined;
        }
    }

    public async setCryptoWalletKeyId(_cryptoWalletId: string, _keyId: number)
    {
        try
        {
            const response = await this.http.post('/api/crypto-wallet/key', {
                wallet_id: _cryptoWalletId,
                key_id: _keyId
            }).toPromise();

            console.debug(`${ nowToTimeStr() }<< /crypto-wallet/key: update:${ _cryptoWalletId } with key: ${ _keyId } => response:${ JSON.stringify(response) }`);
            return response;
        } catch (error)
        {
            console.error(error);
            return undefined;
        }
    }

    public async setCryptoWalletBackupId(_cryptoWalletId: string, _backupId: string)
    {
        try
        {
            const response = await this.http.post('/api/crypto-wallet/backup', {
                cryptoWalletId: _cryptoWalletId,
                backupId: _backupId
            }).toPromise();

            return response;
        } catch (error)
        {
            console.error(error);
            return undefined;
        }
    }

    public async getBackupIds(cryptoWalletId: string): Promise<{ backup_id: string, key_id: number; }>
    {
        try
        {
            const response = await this.http.get<{ backup_id: string, key_id: number; }>
                (`/api/crypto-wallet/backup/${ cryptoWalletId }`).toPromise();

            if (!response) { return null; }
            return response;
        } catch (error)
        {
            console.error(error);
            return undefined;
        }
    }

    public async revokeBackup(_cryptoWalletId: string)
    {
        try
        {
            await this.http.delete(`/api/crypto-wallet/backup/${ _cryptoWalletId }`).toPromise();
        } catch (error)
        {
            console.error(error);
            return undefined;
        }
    }

    public async toggleHidden(_hidden, _walletId)
    {
        try
        {
            await this.http.post(`/api/crypto-wallet/toggle-hidden`, {
                hidden: _hidden,
                walletId: _walletId
            }).toPromise();
        }
        catch (error)
        {
            console.log(error);
            return undefined;
        }
    }

    public async updateCollapsed(_collapsed: boolean, _walletId: string)
    {
        try
        {
            await this.http.post(`/api/crypto-wallet/collapse`, {
                collapsed: _collapsed,
                walletId: _walletId
            }).toPromise();
        }
        catch (error)
        {
            console.log(error);
            return undefined;
        }
    }

    public async updateOrder(_orderIndex, _walletId: string)
    {
        try
        {
            await this.http.post(`/api/crypto-wallet/update-order`, {
                order_index: _orderIndex,
                walletId: _walletId
            }).toPromise();
        }
        catch (error)
        {
            console.log(error);
            return undefined;
        }
    }

    public async updateOrders(data_: IUpdateIndexRequest)
    {
        try
        {
            await this.http.post(`/api/crypto-wallet/update-orders`, data_).toPromise();
        }
        catch (error)
        {
            console.log(error);
            return undefined;
        }
    }

    public async updateBalance(__balanceOff: boolean, _walletId: string)
    {
        try
        {
            await this.http.post(`/api/crypto-wallet/toggle-balance`, {
                balance_off: __balanceOff,
                walletId: _walletId
            }).toPromise();
        }
        catch (error)
        {
            console.log(error);
            return undefined;
        }
    }

    public async update(walletID: string, name: string): Promise<ICryptoWallet>
    {
        try
        {
            return await this.http.patch<ICryptoWallet>(`/api/crypto-wallet/${ walletID }`, {
                name,
                walletId: walletID
            }).toPromise();
        }
        catch (error)
        {
            console.log(error);
            return null;
        }
    }

    public async deleteCryptoWallet(_id: string): Promise<any>
    {
        try
        {
            const response = await this.http.delete(`/api/crypto-wallet/wallet/${ _id }`).toPromise();

            return response;
        } catch (error)
        {
            console.error(error);
            return undefined;
        }
    }

    public async getPrivateDataFromLocal(_cryptoWalletId: string): Promise<string>
    {
        const cryptoWallets = await this.storage.get('crypto-wallets') || [];

        const cryptoWallet = cryptoWallets.find(_cryptoWallet => _cryptoWallet.cryptoWalletId === _cryptoWalletId);

        return Boolean(cryptoWallet) ? JSON.parse(cryptoWallet.data) : null;
    }

    public async setPrivateDataToLocal(_cryptoWalletId: string, _privateData): Promise<any>
    {
        let cryptoWallets = await this.storage.get('crypto-wallets') || [];

        cryptoWallets = cryptoWallets.filter(_cryptoWallet => _cryptoWallet.cryptoWalletId !== _cryptoWalletId);

        cryptoWallets.push({
            cryptoWalletId: _cryptoWalletId,
            data: JSON.stringify(_privateData)
        });

        return await this.storage.set('crypto-wallets', cryptoWallets);
    }

    public async delPrivateDataFromLocal(_cryptoWalletId: string): Promise<any>
    {
        let cryptoWallets = await this.storage.get('crypto-wallets') || [];

        cryptoWallets = cryptoWallets.filter(_cryptoWallet => _cryptoWallet.cryptoWalletId !== _cryptoWalletId);

        return await this.storage.set('crypto-wallets', cryptoWallets);
    }
}
